import { useState, useContext } from "react";
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  TextField,
  Card,
  CardHeader,
  Button,
  Box,
  useMediaQuery,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "../Utility/UserMessageContext";
import authContext from "../Context/AuthProvider";
import { showLoadingImage } from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import { Genders } from "../Utility/Constants";
import { createEnquiry } from "../API/EnquiryApi";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import GenericDatePicker from "../common/GenericDatePicker";

// Static options
const enquirySources = [
  "Doctor Referral",
  "Web Search",
  "Social Media",
  "Happy Patient",
  "Local Event",
  "Advertisement",
  "Employee",
  "Family",
  "Self",
  "Other",
];

const categories = ["Price", "Service", "Support", "Quality"];

function NewEnquiryForm({ tempNewEnquiryId, onNewEnquiryAdded }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(authContext);

  const [form, setForm] = useState({
    name: "",
    countryCodeForPhone: "+91",
    PhoneNumber: "",
    age: "",
    gender: "",
    sourceCategory: "",
    sourceNotes: "",
    enquiryType: "",
    followUpDate: dayjs(),
    status: "",
    query: "",
    facilityId: auth.selectedFacility.id,

  });

  //  [ADDED] - State to hold validation error messages
  const [formErrors, setFormErrors] = useState({
    age: "",
    PhoneNumber: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  function getStyles(option, selectedOptions, theme) {
    return {
      fontWeight: selectedOptions.includes(option)
        ? theme.typography.fontWeightMedium
        : theme.typography.fontWeightRegular,
    };
  }

  //  [MODIFIED] - Add validation to handleChange
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "sourceCategory") {
      setForm((prev) => ({
        ...prev,
        sourceCategory: value,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    //  [ADDED] Validation logic
    switch (name) {
      case "age":
        if (!/^\d*$/.test(value)) {
          setFormErrors((prev) => ({
            ...prev,
            age: "Age must be a number",
          }));
        } else if (+value < 0 || +value > 120) {
          setFormErrors((prev) => ({
            ...prev,
            age: "Age must be between 0 and 120",
          }));
        } else {
          setFormErrors((prev) => ({ ...prev, age: "" }));
        }
        break;

      case "PhoneNumber":
        const phoneRegex = /^\d+$/;
        if (value && !phoneRegex.test(value)) {
          setFormErrors((prev) => ({
            ...prev,
            PhoneNumber: "Phone number must contain only 10 digits",
          }));
        } else {
          setFormErrors((prev) => ({ ...prev, PhoneNumber: "" }));
        }
        break;

      default:
        break;
    }
  };

  const handleSave = async () => {
    const requiredFields = [
      { field: "name", message: "Name is required." },
      { field: "PhoneNumber", message: "Contact number is required." },
      { field: "age", message: "Age is required." },
      { field: "gender", message: "Gender is required." },
      { field: "sourceCategory", message: "source category is required." },
    ];
    setIsSubmitted(true);

    for (const { field, message } of requiredFields) {
      if (!form[field]) {
        showSnackbar(message, "error");
        return;
      }
    }

    // ✅ [ADDED] Block submission if validation errors exist
    if (formErrors.age || formErrors.PhoneNumber) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    try {
      setIsSaving(true);
      console.log("Form submitting", form);
      const result = await createEnquiry(auth, form);
      if (result.isSuccess) {
        showSnackbar("Enquiry saved successfully", "success");
        onNewEnquiryAdded(tempNewEnquiryId);
      } else {
        showSnackbar("Failed to save enquiry: " + "error");
        console.log(result.data);
      }
    } catch (err) {
      console.error("Submission error:", err);
      showSnackbar("Submission failed.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <MenuBar showSave="true" onSave={handleSave} />
      <Box sx={{ position: "relative" }}>
        {isSaving && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(216, 214, 214, 0.4)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1301,
            }}
          >
            {showLoadingImage()}
          </div>
        )}

        <Grid2
          size={12}
          container
          direction="column"
          sx={{ padding: 2 }}
          spacing={1}
        >
          <Grid2
            size={12}
            container
            spacing={1}
            sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
          >
            <Grid2
              size={{ xs: 12, sm: 12, md: 12, lg: 8, xl: 8 }}
              container
              spacing={2}
            >
              <Grid2>
                <Card
                  variant="outlined"
                  sx={{ padding: "10px", width: "100%" }}
                >
                  <CardHeader
                    subheader="Enquiry Details"
                    sx={{ paddingX: 0 }}
                  />
                  <Grid2 container spacing={2}>
                    {/* Name  */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        label="Name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        error={isSubmitted && !form.name}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      />
                    </Grid2>

                     {/* Age */}
                    <Grid2 size={{ xs: 12, md: 3 }}>
                      <TextField
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        label="Age"
                        name="age"
                        type="number"
                        inputProps={{ min: 0, max: 120 }}
                        value={form.age}
                        onChange={handleChange}
                        error={!!formErrors.age || (isSubmitted && !form.age)}
                        helperText={formErrors.age}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      />
                    </Grid2>

                    {/* Gender */}
                    <Grid2 size={{ xs: 12, md: 3 }}>
                      <FormControl
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        error={isSubmitted && !form.gender}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      >
                        <InputLabel id="gender-label">Gender</InputLabel>
                        <Select
                          labelId="gender-label"
                          name="gender"
                          value={form.gender}
                          onChange={handleChange}
                          input={<OutlinedInput label="Gender" />}
                          MenuProps={MenuProps}
                        >
                          {Genders.map((option) => (
                            <MenuItem
                              key={option}
                              value={option}
                              style={getStyles(option, [form.gender], theme)}
                            >
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    {/* Country Code */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        label="Enter Country Code"
                        name="countryCodeForPhone"
                        value={form.countryCodeForPhone}
                        onChange={handleChange}
                        error={isSubmitted && !form.countryCodeForPhone}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      />
                    </Grid2>

                    {/* Phone Number */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        label="Contact Number"
                        name="PhoneNumber"
                        value={form.PhoneNumber}
                        onChange={handleChange}
                        error={
                          !!formErrors.PhoneNumber ||
                          (isSubmitted && !form.PhoneNumber)
                        }
                        helperText={formErrors.PhoneNumber}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      />
                    </Grid2>

                   

                    {/* Main Source */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <FormControl
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        error={isSubmitted && !form.sourceCategory}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      >
                        <InputLabel id="main-source-label">
                          Source Category
                        </InputLabel>
                        <Select
                          labelId="main-source-label"
                          name="sourceCategory"
                          value={form.sourceCategory}
                          onChange={handleChange}
                          input={<OutlinedInput label="Source Category" />}
                          MenuProps={MenuProps}
                        >
                          {enquirySources.map((src) => (
                            <MenuItem
                              key={src}
                              value={src}
                              style={getStyles(
                                src,
                                [form.sourceCategory],
                                theme
                              )}
                            >
                              {src}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    {/* Source Detail */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        margin="dense"
                        size="small"
                        label="Source Detail"
                        name="sourceNotes"
                        value={form.sourceNotes}
                        onChange={handleChange}
                      />
                    </Grid2>

                    {/* enquiryType */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth margin="dense" size="small">
                        <InputLabel id="Enquiry-Type-label">
                          Enquiry Type
                        </InputLabel>
                        <Select
                          labelId="Enquiry-Type-label"
                          name="enquiryType"
                          value={form.enquiryType}
                          onChange={handleChange}
                          input={<OutlinedInput label="Enquiry Type" />}
                          MenuProps={MenuProps}
                        >
                          {categories.map((cat) => (
                            <MenuItem
                              key={cat}
                              value={cat}
                              style={getStyles(cat, [form.enquiryType], theme)}
                            >
                              {cat}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    {/* Follow Up Date */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <GenericDatePicker
                          label="Follow Up Date"
                          name="followUpDate"
                          value={
                            form.followUpDate ? dayjs(form.followUpDate) : null
                          }
                          onChange={(value) => (form.followUpDate = value)}
                          minDate={dayjs(new Date())}
                        />
                      </LocalizationProvider>
                    </Grid2>

                    {/* status */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth margin="dense" size="small">
                        <InputLabel id="Status-label">Status</InputLabel>
                        <Select
                          labelId="Status-label"
                          name="status"
                          value={form.status}
                          onChange={handleChange}
                          input={<OutlinedInput label="Status" />}
                          MenuProps={MenuProps}
                        >
                          {["Open", "Close"].map((sts) => (
                            <MenuItem
                              key={sts}
                              value={sts}
                              style={getStyles(sts, [form.status], theme)}
                            >
                              {sts}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    {/* Description */}
                    <Grid2 size={12}>
                      <TextField
                        fullWidth
                        margin="dense"
                        size="small"
                        multiline
                        rows={4}
                        label="Description"
                        name="query"
                        value={form.query}
                        onChange={handleChange}
                      />
                    </Grid2>
                  </Grid2>
                </Card>
              </Grid2>
            </Grid2>
          </Grid2>
        </Grid2>
      </Box>
    </>
  );
}

export default NewEnquiryForm;
